import React from 'react';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import './SampleAnalysis.css';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, Title, Tooltip, Legend, Filler
);

const SAMPLE_DATA = {
  overall_score: 72,
  overall_assessment: 'Mild asymmetry detected — left forelimb may be compensating',
  horse_name: 'Thunder',
  gait_type: 'trot',
  confidence_by_region: {
    head: 0.94, neck: 0.91, withers: 0.89, pelvis: 0.90,
    front_left: 0.92, front_right: 0.88, hind_left: 0.87, hind_right: 0.85
  },
  gait_metrics: {
    symmetry: { forelimb: 0.84, hindlimb: 0.76, pelvic: 0.79, overall: 0.80 },
    stride_length: { left_front: 1.82, right_front: 1.71, left_hind: 1.65, right_hind: 1.58 },
    range_of_motion: {
      left_front_fetlock: 52.3, right_front_fetlock: 48.7,
      left_hind_fetlock: 61.2, right_hind_fetlock: 55.8,
      left_front_knee: 42.1, right_front_knee: 39.5,
      left_hock: 48.3, right_hock: 44.7
    },
    cadence: { value: 112, unit: 'strides/min' },
    velocity: { estimated: 3.4, unit: 'm/s' },
    phase_relationships: { diagonal_coupling: 0.95, pattern: '2-beat diagonal' }
  },
  abnormalities: [
    {
      severity: 'mild',
      title: 'Left Forelimb Compensation Pattern',
      description: 'The left forelimb shows 6.4% longer stride length compared to the right, suggesting potential right forelimb lameness or compensatory gait.',
      recommendation: 'Consider veterinary assessment for right front hoof, knee, or shoulder. Compare with right forelimb ROM data.'
    },
    {
      severity: 'mild',
      title: 'Right Hindlimb Reduced Propulsion',
      description: 'Right hindlimb shows reduced push-off force indicators. Symmetry score of 0.76 is below the 0.85 threshold for normal soundness.',
      recommendation: 'Evaluate hock, stifle, and sacroiliac region. Right hindlimb lameness often accompanies right forelimb issues.'
    },
    {
      severity: 'moderate',
      title: 'Pelvic Asymmetry — 6° Left Drop',
      description: 'The pelvis tilts 6 degrees to the left during weight-bearing phases, indicating asymmetrical hindlimb loading. This can contribute to back pain.',
      recommendation: 'Consider chiropractic evaluation. Monitor for signs of sacroiliac dysfunction.'
    }
  ]
};

const COLORS = {
  primary: '#2d5016',
  primaryLight: '#3d6b28',
  success: '#3d6b28',
  warning: '#c4842a',
  danger: '#b42318',
  grid: 'rgba(31, 42, 26, 0.06)',
  muted: '#5c6756',
  text: '#1f2a1a'
};

function withAlpha(hex, alpha) {
  const n = hex.replace('#', '');
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function ScoreGauge({ score }) {
  const r = 70;
  const circ = 2 * Math.PI * r;
  const dashOffset = circ - (score / 100) * circ;
  const color = score >= 85 ? COLORS.success : score >= 70 ? COLORS.warning : COLORS.danger;

  return (
    <div className="score-gauge-wrap">
      <svg viewBox="0 0 180 180" className="gauge-svg">
        <circle cx="90" cy="90" r={r} fill="none" stroke="rgba(45,80,22,0.12)" strokeWidth="14"/>
        <circle
          cx="90" cy="90" r={r}
          fill="none"
          stroke={color}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 90 90)"
          style={{ transition: 'stroke-dashoffset 1s ease' }}
        />
      </svg>
      <div className="gauge-center">
        <div className="gauge-number">{score}</div>
        <div className="gauge-max">/ 100</div>
      </div>
    </div>
  );
}

export default function SampleAnalysis() {
  const d = SAMPLE_DATA;
  const sym = d.gait_metrics.symmetry;
  const sl = d.gait_metrics.stride_length;
  const rom = d.gait_metrics.range_of_motion;
  const conf = d.confidence_by_region;

  const symmetryData = {
    labels: ['Forelimb', 'Hindlimb', 'Pelvic'],
    datasets: [{
      label: 'Symmetry Index',
      data: [sym.forelimb, sym.hindlimb, sym.pelvic],
      backgroundColor: [sym.forelimb >= 0.85 ? withAlpha(COLORS.success, 0.28) : withAlpha(COLORS.warning, 0.28),
                       sym.hindlimb >= 0.85 ? withAlpha(COLORS.success, 0.28) : withAlpha(COLORS.danger, 0.28),
                       sym.pelvic >= 0.85 ? withAlpha(COLORS.success, 0.28) : withAlpha(COLORS.danger, 0.28)],
      borderColor: [sym.forelimb >= 0.85 ? COLORS.success : COLORS.warning,
                    sym.hindlimb >= 0.85 ? COLORS.success : COLORS.danger,
                    sym.pelvic >= 0.85 ? COLORS.success : COLORS.danger],
      borderWidth: 2, borderRadius: 8
    }]
  };

  const strideData = {
    labels: ['Left Front', 'Right Front', 'Left Hind', 'Right Hind'],
    datasets: [{
      label: 'Stride Length (m)',
      data: [sl.left_front, sl.right_front, sl.left_hind, sl.right_hind],
      backgroundColor: [withAlpha(COLORS.primaryLight, 0.35), withAlpha(COLORS.primary, 0.35), withAlpha(COLORS.primaryLight, 0.35), withAlpha(COLORS.primary, 0.35)],
      borderColor: COLORS.primary, borderWidth: 2, borderRadius: 6
    }]
  };

  const romData = {
    labels: ['LF Fetlock', 'RF Fetlock', 'LH Fetlock', 'RH Fetlock', 'LF Knee', 'RF Knee', 'LH Hock', 'RH Hock'],
    datasets: [{
      label: 'ROM (degrees)',
      data: [rom.left_front_fetlock, rom.right_front_fetlock, rom.left_hind_fetlock, rom.right_hind_fetlock,
             rom.left_front_knee, rom.right_front_knee, rom.left_hock, rom.right_hock],
      backgroundColor: withAlpha(COLORS.primary, 0.28), borderColor: COLORS.primary, borderWidth: 2, borderRadius: 6
    }]
  };

  const timelineLabels = ['0.0s', '0.3s', '0.7s', '1.0s', '1.3s', '1.7s', '2.0s', '2.3s'];
  const timelineDataMap = [
    [0.0, 0.0, 0.0, 0.0],
    [1.0, 0.2, 0.1, 0.9],
    [0.3, 1.0, 0.95, 0.15],
    [0.85, 0.4, 0.2, 0.85],
    [0.1, 0.9, 0.88, 0.1],
    [0.95, 0.25, 0.05, 0.92],
    [0.2, 0.85, 0.9, 0.12],
    [0.88, 0.35, 0.08, 0.87]
  ];

  const timelineData = {
    labels: timelineLabels,
    datasets: [
      { label: 'Left Front', data: timelineDataMap.map(t => t[0]), borderColor: COLORS.primary, tension: 0.4, fill: false },
      { label: 'Right Front', data: timelineDataMap.map(t => t[1]), borderColor: '#3B82F6', tension: 0.4, fill: false },
      { label: 'Left Hind', data: timelineDataMap.map(t => t[2]), borderColor: COLORS.success, tension: 0.4, fill: false },
      { label: 'Right Hind', data: timelineDataMap.map(t => t[3]), borderColor: '#10B981', tension: 0.4, fill: false }
    ]
  };

  const confData = {
    labels: ['Head', 'Neck', 'Withers', 'Pelvis', 'FL', 'FR', 'HL', 'RH'],
    datasets: [{
      label: 'Confidence',
      data: [conf.head, conf.neck, conf.withers, conf.pelvis, conf.front_left, conf.front_right, conf.hind_left, conf.hind_right],
      backgroundColor: Object.values(conf).map(v => v >= 0.9 ? withAlpha(COLORS.success, 0.35) : v >= 0.8 ? withAlpha(COLORS.warning, 0.35) : withAlpha(COLORS.danger, 0.35)),
      borderColor: Object.values(conf).map(v => v >= 0.9 ? COLORS.success : v >= 0.8 ? COLORS.warning : COLORS.danger),
      borderWidth: 2, borderRadius: 6
    }]
  };

  const chartOptions = (yMax = 1) => ({
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, ...(yMax !== 1 && { max: yMax }), grid: { color: COLORS.grid }, ticks: { color: COLORS.muted } },
      x: { grid: { display: false }, ticks: { color: COLORS.muted } }
    }
  });

  const scoreTagClass = d.overall_score >= 85 ? 'tag-success' : d.overall_score >= 70 ? 'tag-warning' : 'tag-danger';

  return (
    <div className="sample-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <div className="results-header-row">
            <div>
              <h1>Sample Analysis</h1>
              <p className="results-meta">
                <span className="meta-badge">SAMPLE DATA</span>
                <span>{d.horse_name}</span>
                <span>{d.gait_type} gait</span>
                <span>Pre-loaded example for demonstration</span>
              </p>
            </div>
            <Link to="/analyze" className="btn btn-primary">Try It With Your Video</Link>
          </div>
        </div>
      </div>

      <div className="container results-page">

        {/* Score Card */}
        <div className="score-card">
          <ScoreGauge score={d.overall_score} />
          <div className="score-info">
            <h2>{d.overall_assessment}</h2>
            <p className="score-desc">
              This is a sample analysis showing the full Tru-Stride report format.
              Based on sample trot data — 112 strides per minute.
              26 landmarks tracked across 247 frames.
            </p>
            <div className="score-tags">
              <span className={`score-tag ${scoreTagClass}`}>
                {d.overall_score >= 85 ? 'SOUND' : d.overall_score >= 70 ? 'MILD' : 'MODERATE'}: Left Forelimb Compensation Pattern
              </span>
              <span className="score-tag tag-warning">MILD: Right Hindlimb Reduced Propulsion</span>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="results-grid">

          {/* Symmetry */}
          <div className="result-card card-wide">
            <h3>Limb symmetry scores</h3>
            <p className="card-subtitle">Left vs. right limb comparison — 0.85+ = normal soundness threshold</p>
            <div className="chart-container" style={{ height: 220 }}>
              <Bar data={symmetryData} options={chartOptions(1)} />
            </div>
          </div>

          {/* Stride Length */}
          <div className="result-card">
            <h3>Stride length (m)</h3>
            <p className="card-subtitle">Distance per complete stride cycle</p>
            <div className="chart-container" style={{ height: 220 }}>
              <Bar data={strideData} options={chartOptions()} />
            </div>
          </div>

          {/* Range of Motion */}
          <div className="result-card">
            <h3>Range of motion (°)</h3>
            <p className="card-subtitle">Joint flexion during stride cycle</p>
            <div className="chart-container" style={{ height: 220 }}>
              <Bar data={romData} options={chartOptions(70)} />
            </div>
          </div>

          {/* Timeline */}
          <div className="result-card card-wide">
            <h3>Limb position timeline</h3>
            <p className="card-subtitle">Relative vertical position of each limb through the gait cycle</p>
            <div className="chart-container" style={{ height: 220 }}>
              <Line data={timelineData} options={{
                responsive: true, maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                scales: {
                  y: { title: { display: true, text: 'Relative Position', color: COLORS.muted }, grid: { color: COLORS.grid }, ticks: { color: COLORS.muted } },
                  x: { title: { display: true, text: 'Time (s)', color: COLORS.muted }, grid: { color: COLORS.grid }, ticks: { color: COLORS.muted } }
                },
                plugins: { legend: { position: 'bottom', labels: { color: COLORS.muted } } }
              }} />
            </div>
          </div>

          {/* Confidence */}
          <div className="result-card">
            <h3>Detection confidence</h3>
            <p className="card-subtitle">AI confidence by body region</p>
            <div className="chart-container" style={{ height: 220 }}>
              <Bar data={confData} options={{ ...chartOptions(1), indexAxis: 'y' }} />
            </div>
          </div>

          {/* Gait Metrics Table */}
          <div className="result-card">
            <h3>Gait metrics</h3>
            <table className="metrics-table">
              <tbody>
                <tr><td>Cadence</td><td>{d.gait_metrics.cadence.value} {d.gait_metrics.cadence.unit}</td></tr>
                <tr><td>Gait Pattern</td><td>{d.gait_metrics.phase_relationships.pattern}</td></tr>
                <tr><td>Estimated Velocity</td><td>{d.gait_metrics.velocity.estimated} {d.gait_metrics.velocity.unit}</td></tr>
                <tr><td>Diagonal Coupling</td><td>{(d.gait_metrics.phase_relationships.diagonal_coupling * 100).toFixed(0)}%</td></tr>
                <tr><td>Landmarks tracked</td><td>26 / 26</td></tr>
                <tr><td>Frames processed</td><td>247</td></tr>
                <tr><td>Model</td><td>DeepLabCut SuperAnimal</td></tr>
                <tr><td>Processing Time</td><td>8.4s</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Abnormalities */}
        <div className="result-card card-full">
          <h3>Detected findings</h3>
          <div className="abnormalities-list">
            {d.abnormalities.map((ab, i) => (
              <div key={i} className={`abnormality-item ab-${ab.severity}`}>
                <div className="ab-header">
                  <span className={`severity-badge badge-${ab.severity}`}>{ab.severity.toUpperCase()}</span>
                  <h4>{ab.title}</h4>
                </div>
                <p className="ab-description">{ab.description}</p>
                <div className="ab-recommendation"><strong>Recommendation:</strong> {ab.recommendation}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div className="disclaimer">
          <p><strong>Disclaimer:</strong> Sample report with demonstration data. Not a substitute for veterinary examination, diagnosis, or treatment.</p>
        </div>

        {/* Try CTA */}
        <div className="try-card">
          <h3>Ready to analyze your horse?</h3>
          <p>Upload your own video and get a personalized biomechanical analysis.</p>
          <Link to="/analyze" className="btn btn-primary btn-lg">Upload Your Video →</Link>
        </div>

      </div>
    </div>
  );
}
